import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { downloadUrl, useDownloads, useEnqueue, type Job } from "@/hooks/useDownloads";
import { useCurrentUser } from "@/hooks/useAuth";
import { usePresetsInfo } from "@/hooks/usePresetsInfo";
import {
  useTelegramLinkStatus,
  useTelegramStatus,
} from "@/hooks/useTelegram";
import { trackDownload, trackPresetChange } from "@/analytics/analytics";
import { CopyButton } from "./CopyButton";
import { TelegramSendButton } from "./TelegramSendButton";

function shareUrl(postId: number, preset: string): string {
  return `${window.location.origin}/api/v1/files/${postId}/${preset}`;
}

// Presets exposed by the existing dropdown for power users. The two new
// shortcut buttons cover the common cases.
const PRESETS = ["gif", "gif_hq", "gif_lite", "mp4_source", "mp4_compact"] as const;
type Preset = (typeof PRESETS)[number];

// Maps the friendly-named shortcut buttons to actual backend presets.
const SHORTCUTS: { preset: Preset; label: string }[] = [
  { preset: "gif", label: "GIF" },
  { preset: "mp4_compact", label: "MP4" },
];

const STATUS_COLORS: Record<string, string> = {
  queued: "text-zinc-400",
  downloading: "text-blue-400",
  transcoding: "text-yellow-400",
  done: "text-green-400",
  failed: "text-red-400",
};

interface ShortcutButtonProps {
  postId: number;
  duration: number | null;
  preset: Preset;
  label: string;
  jobs: Job[];
  isLoggedIn: boolean;
  maxInputSeconds: number | undefined;
}

/** One-click "convert + copy" button.
 *
 * Behaviours:
 * - GIF preset + source longer than ``max_input_seconds``: disabled with a
 *   "too long" tooltip. (MP4 doesn't have the same size-explosion problem,
 *   so it stays available.)
 * - Anonymous user + no done job: disabled with a "sign in" tooltip.
 * - Done job already exists: click copies the share URL instantly.
 * - Logged-in user + no done job: click kicks off the conversion; the
 *   button label shifts to "Converting… N%" while the job runs and
 *   auto-copies the share URL the moment it lands as done. */
function ShortcutButton({
  postId,
  duration,
  preset,
  label,
  jobs,
  isLoggedIn,
  maxInputSeconds,
}: ShortcutButtonProps) {
  const { mutate: enqueue, isPending: isEnqueuing } = useEnqueue();
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const job = jobs.find((j) => j.post_id === postId && j.preset === preset);
  const done = job?.status === "done";
  const failed = job?.status === "failed";
  const isGif = preset.startsWith("gif");
  const tooLong =
    isGif &&
    maxInputSeconds !== undefined &&
    duration !== null &&
    duration > maxInputSeconds;
  // Track in-flight only when the user kicked off conversion from this button
  // (so we don't auto-copy after every random job done elsewhere).
  const inFlight =
    pendingJobId !== null && job?.id === pendingJobId && !done && !failed;

  // Auto-copy when our pending job finishes.
  useEffect(() => {
    if (!pendingJobId || !job || job.id !== pendingJobId) return;
    if (job.status === "done") {
      navigator.clipboard.writeText(shareUrl(postId, preset)).catch(() => {});
      setCopied(true);
      setPendingJobId(null);
      window.setTimeout(() => setCopied(false), 1500);
    } else if (job.status === "failed") {
      setPendingJobId(null);
    }
  }, [pendingJobId, job, postId, preset]);

  async function onClick() {
    if (done) {
      // Just copy — no need to enqueue.
      try {
        await navigator.clipboard.writeText(shareUrl(postId, preset));
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      } catch {
        // Silent fallback — clipboard rejected.
      }
      return;
    }
    if (!isLoggedIn) return; // disabled state, should not fire
    const format = preset.startsWith("mp4") ? "mp4" : "gif";
    trackDownload({ format, preset });
    enqueue(
      { post_id: postId, preset },
      { onSuccess: (j: Job) => setPendingJobId(j.id) },
    );
  }

  // Resolve label + disabled + tooltip into one place.
  let buttonLabel: string;
  let disabled = false;
  let tooltip: string | undefined;

  if (copied) {
    buttonLabel = `✓ Copied ${label}`;
  } else if (tooLong) {
    buttonLabel = `${label} too long`;
    disabled = true;
    tooltip = `Source is ${Math.round(duration!)}s, GIF cap is ${maxInputSeconds}s. Try MP4 instead.`;
  } else if (inFlight) {
    buttonLabel =
      job && job.progress > 0
        ? `Converting ${label} ${job.progress}%`
        : `Converting ${label}…`;
    disabled = true;
  } else if (done) {
    buttonLabel = `Copy ${label} link`;
  } else if (isLoggedIn) {
    buttonLabel = `Convert + copy ${label}`;
  } else {
    buttonLabel = `Copy ${label} link`;
    disabled = true;
    tooltip = "Sign in to create this conversion.";
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled || isEnqueuing}
      title={tooltip}
      className={`text-xs font-semibold px-3 py-2 min-h-[36px] rounded transition-colors flex-1 ${
        disabled
          ? "bg-surface-2 text-zinc-600 cursor-not-allowed"
          : copied
            ? "bg-green-800 text-white"
            : "bg-surface-2 hover:bg-surface-3 text-zinc-200"
      }`}
    >
      {buttonLabel}
    </button>
  );
}

interface EnqueueFormProps {
  postId: number;
  duration: number | null;
  maxInputSeconds: number | undefined;
}

function EnqueueForm({ postId, duration, maxInputSeconds }: EnqueueFormProps) {
  const [preset, setPreset] = useState<Preset>("gif");
  const { mutate: enqueue, isPending } = useEnqueue();

  const isGif = preset.startsWith("gif");
  const tooLong =
    isGif &&
    maxInputSeconds !== undefined &&
    duration !== null &&
    duration > maxInputSeconds;

  function handlePresetChange(p: Preset) {
    setPreset(p);
    trackPresetChange(p);
  }

  function handleDownload() {
    const format = preset.startsWith("mp4") ? "mp4" : "gif";
    enqueue({ post_id: postId, preset });
    trackDownload({ format, preset });
  }

  return (
    <div className="space-y-1">
      <div className="flex gap-2 items-center flex-wrap">
        <select
          value={preset}
          onChange={(e) => handlePresetChange(e.target.value as Preset)}
          className="bg-surface-2 border border-surface-3 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-accent"
        >
          {PRESETS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <button
          onClick={handleDownload}
          disabled={isPending || tooLong}
          title={
            tooLong
              ? `Source is ${Math.round(duration!)}s, GIF cap is ${maxInputSeconds}s.`
              : undefined
          }
          className="bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-1.5 rounded transition-colors"
        >
          {isPending ? "Queuing…" : "Download as…"}
        </button>
      </div>
      {tooLong && (
        <p className="text-[10px] text-amber-500/80">
          This GIF preset would explode in size; only MP4 presets are allowed
          for sources over {maxInputSeconds}s.
        </p>
      )}
    </div>
  );
}

interface Props {
  postId?: number;
  duration?: number | null;
}

export function DownloadPanel({ postId, duration = null }: Props) {
  const { data: jobs = [], isLoading } = useDownloads();
  const { data: user } = useCurrentUser();
  const { data: presetsInfo } = usePresetsInfo();
  const { data: telegramStatus } = useTelegramStatus();
  const telegramEnabled = !!user && !!telegramStatus?.configured;
  const { data: telegramLink } = useTelegramLinkStatus(telegramEnabled);
  const dmAvailable = telegramEnabled && !!telegramLink?.linked;
  const maxInputSeconds = presetsInfo?.max_input_seconds;

  const relevantJobs = postId ? jobs.filter((j) => j.post_id === postId) : jobs;

  return (
    <div className="space-y-3">
      {postId && (
        <div className="pb-3 border-b border-surface-3 space-y-3">
          {/* Shortcut buttons: covers the 95% case in one click. */}
          <div className="flex gap-2">
            {SHORTCUTS.map((s) => (
              <ShortcutButton
                key={s.preset}
                postId={postId}
                duration={duration}
                preset={s.preset}
                label={s.label}
                jobs={jobs}
                isLoggedIn={!!user}
                maxInputSeconds={maxInputSeconds}
              />
            ))}
          </div>
          {/* Dropdown for power users who want a specific variant. */}
          {user ? (
            <details>
              <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-300">
                More presets…
              </summary>
              <div className="mt-2">
                <EnqueueForm
                  postId={postId}
                  duration={duration}
                  maxInputSeconds={maxInputSeconds}
                />
              </div>
            </details>
          ) : (
            <p className="text-xs text-zinc-500 leading-relaxed">
              <Link to="/login" className="text-accent-light hover:underline">
                Sign in
              </Link>{" "}
              to create new conversions. Existing ones below stay
              downloadable and shareable by anyone with the link.
            </p>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-zinc-600 text-sm">Loading…</p>
      ) : relevantJobs.length === 0 ? (
        <p className="text-zinc-600 text-sm">No downloads yet</p>
      ) : (
        <ul className="space-y-2">
          {relevantJobs.map((job) => (
            <li key={job.id} className="bg-surface-2 rounded-lg p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-zinc-300">post #{job.post_id}</span>
                    <span className="text-zinc-500">{job.preset}</span>
                  </div>
                  <div className={`text-xs mt-0.5 ${STATUS_COLORS[job.status] ?? "text-zinc-400"}`}>
                    {job.status}
                    {job.progress > 0 && job.status !== "done" && ` (${job.progress}%)`}
                  </div>
                  {job.error_msg && (
                    <div className="text-xs text-red-400 mt-1 truncate">{job.error_msg}</div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {job.status === "done" && (
                    <>
                      <a
                        href={downloadUrl(job.id)}
                        download
                        className="text-xs bg-green-800 hover:bg-green-700 text-white px-2 py-1 min-h-[32px] rounded transition-colors inline-flex items-center"
                      >
                        ↓ Download
                      </a>
                      <CopyButton
                        text={shareUrl(job.post_id, job.preset)}
                        label="Share link"
                        hint="Permanent URL to this conversion — anyone with the link can download it."
                        className="text-[11px] text-zinc-400 hover:text-accent-light min-h-[28px] inline-flex items-center"
                      />
                      {telegramEnabled && (
                        <TelegramSendButton
                          postId={job.post_id}
                          preset={job.preset}
                          channelUrl={telegramStatus?.channel_url ?? null}
                          variant="compact"
                          target="channel"
                        />
                      )}
                      {dmAvailable && (
                        <TelegramSendButton
                          postId={job.post_id}
                          preset={job.preset}
                          channelUrl={null}
                          variant="compact"
                          target="dm"
                        />
                      )}
                    </>
                  )}
                </div>
              </div>

              {job.progress > 0 && job.status !== "done" && job.status !== "failed" && (
                <div className="mt-2 h-1 bg-surface-3 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent transition-all"
                    style={{ width: `${job.progress}%` }}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

